import React, { useState, useEffect } from "react";
import Layout from "../../layout/builders/Layout";
import {
  Plus,
  Trash2,
  Edit,
  Briefcase,
  List,
  Loader2,
  Power,
  ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";
import RichTextEditor from "../../components/common/RichTextEditor";
import useJobStore from "../../store/builders/useJobStore";
import ConfirmationPopup from "../../components/common/ConfirmationPopup";
import { motion, AnimatePresence } from "framer-motion";

const experienceOptions = [
  "0-1 years",
  "1-2 years",
  "3-5 years",
  "5+ years",
  "Any",
];

const salaryTypeOptions = [
  "per month",
  "per day",
];

const initialFormState = {
  title: "",
  positions: "",
  experience: "1-2 years",
  jobType: "Full time",
  workDays: "5",
  shiftTiming: "",
  salaryAmount: "",
  salaryType: "per month",
  benefits: {
    accommodation: false,
    food: false,
    insurance: false,
    travel: false,
    allowance: false,
  },
  gender: "Any",
  ageLimit: "",
  description: "",
  notes: "",
};

// Helper to map frontend form state to backend API payload
const mapFormToApi = (formData) => ({
  job_title: formData.title,
  number_of_positions: parseInt(formData.positions, 10),
  // Ensure correct mapping regardless of legacy values:
  experience_required: experienceOptions.includes(formData.experience)
    ? formData.experience
    : "1-2 years",
  job_type: formData.jobType,
  work_days: `${formData.workDays} days / week`,
  work_shift: formData.shiftTiming,
  salary_amount: parseFloat(formData.salaryAmount) || null,
  salary_type: salaryTypeOptions.includes(formData.salaryType)
    ? formData.salaryType
    : "per month",
  gender_preference: formData.gender,
  age_limit: parseInt(formData.ageLimit, 10) || null,
  benefits: formData.benefits,
  job_description: formData.description,
  additional_notes: formData.notes,
});

// Helper to map backend API response to frontend form state for editing
const mapApiToForm = (job) => ({
  title: job.job_title || "",
  positions: job.number_of_positions?.toString() || "",
  // Always fallback to a correct ENUM value:
  experience: experienceOptions.includes(job.experience_required)
    ? job.experience_required
    : "1-2 years",
  jobType: job.job_type || "Full time",
  workDays: job.work_days ? job.work_days.split(" ")[0] : "5",
  shiftTiming: job.work_shift || "",
  salaryAmount: job.salary_amount
    ? parseFloat(job.salary_amount).toString()
    : "",
  salaryType: salaryTypeOptions.includes(job.salary_type)
    ? job.salary_type
    : "per month",
  benefits: {
    accommodation: !!job.has_accommodation,
    food: !!job.has_food,
    insurance: !!job.has_insurance,
    travel: !!job.has_travel,
    allowance: !!job.has_allowance,
  },
  gender: job.gender_preference || "Any",
  ageLimit: job.age_limit?.toString() || "",
  description: job.job_description || "",
  notes: job.additional_notes || "",
});

const getBenefitText = (benefits) => {
  const benefitList = Object.entries(benefits)
    .filter(([, value]) => value)
    .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1));

  if (benefitList.length === 0) return "None";
  return benefitList.join(", ");
};

const Jobs = () => {
  const {
    jobs,
    fetchJobs,
    createJob,
    updateJob,
    deleteJob,
    updateJobStatus,
    isLoading,
    isSubmitting,
  } = useJobStore();

  const [formData, setFormData] = useState(initialFormState);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [jobToUpdateStatus, setJobToUpdateStatus] = useState(null);
  const [isConfirmStatusChangeOpen, setIsConfirmStatusChangeOpen] =
    useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [expandedJobId, setExpandedJobId] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBenefitChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      benefits: { ...prev.benefits, [name]: checked },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.positions || !formData.description) {
      toast.error(
        "Please fill required fields: Title, Positions, and Description."
      );
      return;
    }
    const apiPayload = mapFormToApi(formData);
    const result = await createJob(apiPayload);
    if (result.success) {
      setFormData(initialFormState);
      setIsFormVisible(false);
    }
  };

  const handleDeleteClick = (job) => {
    setJobToDelete(job);
    setIsConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (jobToDelete) {
      const result = await deleteJob(jobToDelete.id);
      if (result.success) {
        setIsConfirmDeleteOpen(false);
        setJobToDelete(null);
      }
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    const formValues = mapApiToForm(job);
    setFormData(formValues);
    setIsEditMode(true);
    setIsFormVisible(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.positions || !formData.description) {
      toast.error(
        "Please fill required fields: Title, Positions, and Description."
      );
      return;
    }

    const apiPayload = mapFormToApi(formData);
    const result = await updateJob(editingJob.id, apiPayload);
    if (result.success) {
      setFormData(initialFormState);
      setEditingJob(null);
      setIsEditMode(false);
      setIsFormVisible(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData(initialFormState);
    setEditingJob(null);
    setIsEditMode(false);
    setIsFormVisible(false);
  };

  const handleCreateNewClick = () => {
    if (isEditMode) {
      handleCancelEdit();
    }
    setIsFormVisible(true);
  };

  const handleStatusToggleClick = (job) => {
    const newStatusToSet = job.status === "Open" ? "Closed" : "Open";
    setJobToUpdateStatus(job);
    setNewStatus(newStatusToSet);
    setIsConfirmStatusChangeOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (jobToUpdateStatus && newStatus) {
      const result = await updateJobStatus(jobToUpdateStatus.id, newStatus);
      if (result.success) {
        setIsConfirmStatusChangeOpen(false);
        setJobToUpdateStatus(null);
        setNewStatus("");
      }
    }
  };

  const handleExpandToggle = (jobId) => {
    setExpandedJobId((prevId) => (prevId === jobId ? null : jobId));
  };

  return (
    <>
      <ConfirmationPopup
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the job posting for "${jobToDelete?.job_title}"?`}
        confirmText="Delete"
        isLoading={isSubmitting}
      />
      <ConfirmationPopup
        isOpen={isConfirmStatusChangeOpen}
        onClose={() => setIsConfirmStatusChangeOpen(false)}
        onConfirm={handleConfirmStatusChange}
        title={`Confirm Status Change`}
        message={`Are you sure you want to change the status of "${jobToUpdateStatus?.job_title}" to "${newStatus}"?`}
        confirmText="Confirm"
        isLoading={isSubmitting}
      />
      <div style={{ backgroundColor: "#E0E0E0" }}>
        <Layout>
          <div className="space-y-6">
            {/* Job Post Form Section */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-500" />
                  {isEditMode ? "Edit Job" : "Post a New Job"}
                </h2>
                <div className="flex gap-2">
                  {isEditMode && (
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition"
                      disabled={isSubmitting}
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (isFormVisible && !isEditMode) {
                        setIsFormVisible(false);
                      } else {
                        handleCreateNewClick();
                      }
                    }}
                    className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 transition"
                  >
                    {isFormVisible && !isEditMode
                      ? "Hide Form"
                      : "Create New Job"}
                  </button>
                </div>
              </div>

              {isFormVisible && (
                <form
                  onSubmit={isEditMode ? handleUpdate : handleSubmit}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                  {/* Job Title */}
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Job Title / Designation{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      required
                    />
                  </div>
                  {/* Number of Positions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Number of Positions{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="positions"
                      value={formData.positions}
                      onChange={handleInputChange}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      required
                    />
                  </div>
                  {/* Experience */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Experience
                    </label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    >
                      {experienceOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  {/* Job Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Job Type
                    </label>
                    <select
                      name="jobType"
                      value={formData.jobType}
                      onChange={handleInputChange}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    >
                      <option>Full time</option>
                      <option>Part time</option>
                      <option>Contract</option>
                      <option>Internship</option>
                    </select>
                  </div>
                  {/* Work Days */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Work Days
                    </label>
                    <select
                      name="workDays"
                      value={formData.workDays}
                      onChange={handleInputChange}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    >
                      <option value="5">5 days / week</option>
                      <option value="6">6 days / week</option>
                      <option value="7">Flexible</option>
                    </select>
                  </div>
                  {/* Shift Timing */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Working Hour / Shift Timing
                    </label>
                    <input
                      type="text"
                      name="shiftTiming"
                      value={formData.shiftTiming}
                      onChange={handleInputChange}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      placeholder="e.g., 9 AM - 5 PM"
                    />
                  </div>
                  {/* Salary */}
                  <div className="flex items-end gap-2">
                    <div className="flex-grow">
                      <label className="block text-sm font-medium text-gray-700">
                        Salary Amount
                      </label>
                      <input
                        type="number"
                        name="salaryAmount"
                        value={formData.salaryAmount}
                        onChange={handleInputChange}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    <select
                      name="salaryType"
                      value={formData.salaryType}
                      onChange={handleInputChange}
                      className="p-2 border border-gray-300 rounded-md shadow-sm"
                    >
                      {salaryTypeOptions.map((option) => (
                        <option key={option} value={option}>{option.charAt(0).toUpperCase() + option.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  {/* Benefits */}
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Benefits
                    </label>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                      {Object.keys(formData.benefits).map((key) => (
                        <label key={key} className="flex items-center">
                          <input
                            type="checkbox"
                            name={key}
                            checked={formData.benefits[key]}
                            onChange={handleBenefitChange}
                            className="rounded"
                          />
                          <span className="ml-2 text-sm text-gray-600 capitalize">
                            {key === "travel" ? "Travel/Bus" : key}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {/* Gender */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Gender Preference
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                    >
                      <option>Any</option>
                      <option>Male</option>
                      <option>Female</option>
                    </select>
                  </div>
                  {/* Age Limit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Age Limit
                    </label>
                    <input
                      type="number"
                      name="ageLimit"
                      value={formData.ageLimit}
                      onChange={handleInputChange}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
                      placeholder="e.g., 40"
                    />
                  </div>
                  {/* Job Description */}
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Job Description <span className="text-red-500">*</span>
                    </label>
                    <RichTextEditor
                      value={formData.description}
                      onChange={(content) =>
                        setFormData((prev) => ({
                          ...prev,
                          description: content,
                        }))
                      }
                      placeholder="Describe the job requirements and responsibilities..."
                      height={200}
                    />
                  </div>
                  {/* Additional Notes */}
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Additional Notes / Expectation
                    </label>
                    <RichTextEditor
                      value={formData.notes}
                      onChange={(content) =>
                        setFormData((prev) => ({ ...prev, notes: content }))
                      }
                      placeholder="Add any additional notes, expectations, or special requirements..."
                      height={150}
                    />
                  </div>
                  {/* Actions */}
                  <div className="md:col-span-2 lg:col-span-3 flex justify-end gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData(initialFormState)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                      disabled={isSubmitting}
                    >
                      Clear
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center gap-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Plus />
                      )}
                      {isEditMode ? "Update Job" : "Post Job"}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Posted Jobs List */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <List className="w-5 h-5 text-blue-500" />
                Your Posted Jobs
              </h2>
              {isLoading ? (
                <div className="flex justify-center items-center py-10">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <div className="space-y-4">
                  {jobs.length > 0 ? (
                    jobs.map((job) => (
                      <div
                        key={job.id}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <div className="p-4 bg-gray-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="text-lg font-semibold text-gray-800">
                                {job.job_title}
                              </h3>
                              <span
                                className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                  job.status === "Open"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {job.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {job.job_type} | {job.experience_required} |{" "}
                              {job.number_of_positions} position(s)
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleExpandToggle(job.id)}
                              className="p-2 text-gray-500 hover:bg-gray-200 rounded-md"
                              title="View Details"
                            >
                              <ChevronDown
                                className={`w-5 h-5 transition-transform ${
                                  expandedJobId === job.id ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                            <button
                              onClick={() => handleStatusToggleClick(job)}
                              className={`p-2 rounded-md transition-colors ${
                                job.status === "Open"
                                  ? "text-gray-500 hover:bg-red-100 hover:text-red-600"
                                  : "text-gray-500 hover:bg-green-100 hover:text-green-600"
                              }`}
                              title={
                                job.status === "Open"
                                  ? "Close Job"
                                  : "Re-open Job"
                              }
                            >
                              <Power className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(job)}
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-md"
                              title="Edit job"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(job)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-md"
                              title="Delete job"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <AnimatePresence>
                          {expandedJobId === job.id && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="p-6 border-t border-gray-200 space-y-4 text-sm">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                  <p>
                                    <strong className="font-semibold text-gray-700">
                                      Salary:
                                    </strong>{" "}
                                    ₹{job.salary_amount} / {job.salary_type}
                                  </p>
                                  <p>
                                    <strong className="font-semibold text-gray-700">
                                      Work Days:
                                    </strong>{" "}
                                    {job.work_days}
                                  </p>
                                  <p>
                                    <strong className="font-semibold text-gray-700">
                                      Shift:
                                    </strong>{" "}
                                    {job.work_shift || "N/A"}
                                  </p>
                                  <p>
                                    <strong className="font-semibold text-gray-700">
                                      Gender:
                                    </strong>{" "}
                                    {job.gender_preference}
                                  </p>
                                  <p>
                                    <strong className="font-semibold text-gray-700">
                                      Age Limit:
                                    </strong>{" "}
                                    {job.age_limit || "N/A"}
                                  </p>
                                </div>
                                <p>
                                  <strong className="font-semibold text-gray-700">
                                    Benefits:
                                  </strong>{" "}
                                  {getBenefitText({
                                    accommodation: job.has_accommodation,
                                    food: job.has_food,
                                    insurance: job.has_insurance,
                                    travel: job.has_travel,
                                    allowance: job.has_allowance,
                                  })}
                                </p>
                                <div>
                                  <strong className="block font-semibold text-gray-700 mb-1">
                                    Description:
                                  </strong>
                                  <div
                                    className="prose prose-sm max-w-none text-gray-600"
                                    dangerouslySetInnerHTML={{
                                      __html: job.job_description,
                                    }}
                                  />
                                </div>
                                {job.additional_notes && (
                                  <div>
                                    <strong className="block font-semibold text-gray-700 mb-1">
                                      Notes:
                                    </strong>
                                    <div
                                      className="prose prose-sm max-w-none text-gray-600"
                                      dangerouslySetInnerHTML={{
                                        __html: job.additional_notes,
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-4">
                      You haven't posted any jobs yet.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </Layout>
      </div>
    </>
  );
};

export default Jobs;
